import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, WifiOff, ArrowUpRight, Quote } from 'lucide-react';
export function SlideQrAisle() {
  const columns = [{
    title: 'What worked',
    icon: CheckCircle2,
    accent: 'text-emerald-500',
    bg: 'bg-emerald-50',
    border: 'border-emerald-100',
    items: ['QR-led access generated strong volume — the flyer/QR route drove the majority of participation.', 'The quality of response was notably high, not just the quantity.', 'Attendees who entered were willing to answer strategic questions in full.', 'Scan-based curiosity converted into 12 qualified leads.', 'Generated rich market intelligence and brand perception data.']
  }, {
    title: 'Why the aisle layer didn’t run',
    icon: WifiOff,
    accent: 'text-amber-500',
    bg: 'bg-amber-50',
    border: 'border-amber-100',
    items: ['The original intent was for booth staff to meet attendees in the aisle and open the Snak live.', 'Poor venue internet — and insufficient own cell coverage — made live booth use unreliable.', 'As a result, the human aisle-breaker layer could not be activated as planned.', 'This was a venue connectivity constraint, not a failure of the concept.']
  }, {
    title: 'Improvement opportunity',
    icon: ArrowUpRight,
    accent: 'text-minerva-blue',
    bg: 'bg-minerva-blue/5',
    border: 'border-minerva-blue/20',
    items: ['Secure reliable connectivity: a dedicated hotspot and offline-capable, pre-loaded iPads.', 'Keep the QR mechanic — it worked — and add the live booth layer on top.', 'Use the Snak as a guided prompt reps can open with, turning it into a live conversation tool.']
  }];
  return <div className="flex flex-col h-full max-w-[1600px] mx-auto w-full" data-id="element-828">
      <div className="mb-7" data-id="element-829">
        <h2 className="text-4xl font-display font-bold text-slate-900 mb-4" data-id="element-830">
          From strong QR activation to true aisle breaker
        </h2>
        <p className="text-xl text-slate-500 max-w-4xl" data-id="element-831">
          The aisle layer relied on booth staff opening the Snak live — which
          venue connectivity made unreliable. The QR route still delivered
          strong volume and high-quality responses; the next step is to make the
          booth layer dependable so both can run together.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1" data-id="element-832">
        {columns.map((col, i) => {
        const Icon = col.icon;
        return <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2 + i * 0.12
        }} className={`bg-white rounded-3xl p-7 border ${col.border} shadow-sm flex flex-col`} data-id="element-833">
              <div className="flex items-center gap-3 mb-6" data-id="element-834">
                <div className={`p-2.5 rounded-xl ${col.bg} ${col.accent}`} data-id="element-835">
                  <Icon className="w-5 h-5" data-id="element-836" />
                </div>
                <h3 className="text-lg font-bold text-slate-800" data-id="element-837">
                  {col.title}
                </h3>
              </div>
              <ul className="space-y-3.5 flex-1" data-id="element-838">
                {col.items.map((item, j) => <motion.li key={j} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.3 + i * 0.12 + j * 0.06
            }} className="flex items-start gap-3" data-id="element-839">
                    <div className={`w-1.5 h-1.5 rounded-full mt-2 shrink-0 ${col.accent.replace('text-', 'bg-')}`} data-id="element-840" />
                    <span className="text-sm text-slate-600 leading-relaxed" data-id="element-841">
                      {item}
                    </span>
                  </motion.li>)}
              </ul>
            </motion.div>;
      })}
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.9
    }} className="mt-6 bg-minerva-dark text-white rounded-2xl p-6 flex gap-5 items-center" data-id="element-842">
        <div className="p-3 bg-white/10 rounded-full shrink-0 text-minerva-cyan" data-id="element-843">
          <Quote className="w-6 h-6" data-id="element-844" />
        </div>
        <p className="text-base md:text-lg leading-relaxed text-white/90" data-id="element-845">
          Even without the live booth layer, the Snak converted QR curiosity
          into strong volume, high-quality responses, leads, and insight. With
          reliable connectivity next time, the booth team can finally run it
          live — so it becomes not only something attendees scan, but something{' '}
          <span className="text-minerva-cyan font-semibold" data-id="element-846">
            Minerva uses to start better conversations in the aisle.
          </span>
        </p>
      </motion.div>
    </div>;
}